/**
 * TOSS Web Application - Permissions Constants
 *
 * Granular permission keys system. Source of truth: backend
 * `GET /api/lk/permissions/my-keys` returns the `Set<string>` of keys the
 * current user holds; that set is loaded into `permissionStore` and read
 * via `usePermission(key)` / `useAnyPermission(...)` hooks.
 *
 * The `Module` enum + `MODULE_GROUPS` exports are kept because they're still
 * used by some grouping UI (sidebar dropdowns, badge styling) — they are NOT
 * tied to permission checks anymore.
 */

// ============================================================================
// Modules — labels & grouping (no longer drive permissions)
// ============================================================================

export enum Module {
  Applications = 'applications',
  Tasks = 'tasks',
  OwnExpenses = 'ownExpenses',
  AllExpenses = 'allExpenses',
  Workers = 'workers',
  Customers = 'customers',
  Containers = 'containers',
  Equipment = 'equipment',
  Services = 'services',
  PTODirectory = 'ptoDirectory',
  Directions = 'directions',
  Materials = 'materials',
  Instructions = 'instructions',
  Profile = 'profile',
  Messenger = 'messenger',
  Fmo = 'fmo',
  AuditLogs = 'auditLogs',
  EmailGateway = 'emailGateway',
}

export const ModuleLabels: Record<Module, string> = {
  [Module.Applications]: 'Заявки',
  [Module.Tasks]: 'Задачи',
  [Module.OwnExpenses]: 'Расходы (свои)',
  [Module.AllExpenses]: 'Расходы (все)',
  [Module.Workers]: 'Сотрудники',
  [Module.Customers]: 'Клиенты',
  [Module.Containers]: 'Контейнеры',
  [Module.Equipment]: 'Энергетическая установка',
  [Module.Services]: 'Услуги',
  [Module.PTODirectory]: 'ПТО справочник',
  [Module.Directions]: 'Направления',
  [Module.Materials]: 'Материалы',
  [Module.Instructions]: 'Инструкции',
  [Module.Profile]: 'Профиль',
  [Module.Messenger]: 'Мессенджер',
  [Module.Fmo]: 'ФМО',
  [Module.AuditLogs]: 'Журнал аудита',
  [Module.EmailGateway]: 'Почтовый шлюз',
};

export const MODULE_GROUPS = {
  main: [Module.Applications, Module.Tasks, Module.OwnExpenses, Module.Messenger],
  finance: [Module.AllExpenses, Module.Fmo],
  directory: [
    Module.Customers,
    Module.Containers,
    Module.Equipment,
    Module.Services,
    Module.PTODirectory,
    Module.Directions,
    Module.Materials,
    Module.Instructions,
  ],
  users: [Module.Workers],
  personal: [Module.Profile],
  admin: [Module.AuditLogs, Module.EmailGateway],
} as const;

// ============================================================================
// Granular Permission Keys
// ============================================================================

export type PermissionKey = string;

export interface PermissionDescriptor {
  key: PermissionKey;
  module: string;
  action: string;
  displayName: string;
  isTabVisibility: boolean;
}

/**
 * Typed constants for every permission key. Mirrors `PermissionKeys.cs` on the
 * backend. Use with `usePermission(PK.X.Y)` for autocomplete + type safety.
 */
export const PK = {
  Applications: {
    View: 'applications.view',
    Create: 'applications.create',
    Edit: 'applications.edit',
    // Note: applications.delete removed — заявки отменяются (Applications.Cancel), не удаляются.
    Cancel: 'applications.cancel',
    /** Приёмка заявки, созданной клиентом в ЛК (New → WorkMechanic). */
    Accept: 'applications.accept',
    /** Объединение заявок одного клиента до начала работ (application-merge spec). */
    Merge: 'applications.merge',
    TrackingUpdate: 'applications.tracking.update',
    ExpensesExport: 'applications.expenses.export',
    ReportDownload: 'applications.report.download',
    HistoryView: 'applications.history.view',
    /** Версии заявки: список, «что изменилось», «как было» (application-versions spec, D8). */
    VersionsView: 'applications.versions.view',
    /** Откат ОДНОЙ заявки к выбранной версии — начальник диспетчерской, шеф, администратор. */
    VersionsRestore: 'applications.versions.restore',
    PtoAdd: 'applications.pto.add',
    PtoEdit: 'applications.pto.edit',
    PtoRemove: 'applications.pto.remove',
    PtoAssignMechanic: 'applications.pto.assign_mechanic',
    InspectionsDispatcherFill: 'applications.inspections.dispatcher_fill',
    ExpensesAdd: 'applications.expenses.add',
  },
  Tasks: {
    ViewOwn: 'tasks.view_own',
    ViewAll: 'tasks.view_all',
    Create: 'tasks.create',
    Edit: 'tasks.edit',
    Assign: 'tasks.assign',
    Delete: 'tasks.delete',
    // Note: tasks.complete removed — completion = POST /tasks/{id}/report?markAsCompleted=true.
    ReportView: 'tasks.report.view',
    ReportSubmit: 'tasks.report.submit',
    Cancel: 'tasks.cancel',
  },
  Expenses: {
    OwnView: 'expenses.own.view',
    OwnCreate: 'expenses.own.create',
    OwnEdit: 'expenses.own.edit',
    OwnDelete: 'expenses.own.delete',
    AllView: 'expenses.all.view',
    Approve: 'expenses.approve',
    Reject: 'expenses.reject',
    Export: 'expenses.export',
  },
  Workers: {
    View: 'workers.view',
    Create: 'workers.create',
    Edit: 'workers.edit',
    Block: 'workers.block',
    DeleteRequestApprove: 'workers.delete_request.approve',
    DeleteRequestReject: 'workers.delete_request.reject',
  },
  Customers: {
    View: 'customers.view',
    Create: 'customers.create',
    Edit: 'customers.edit',
    Delete: 'customers.delete',
    /** Client portal — internal LK account management on the customer card (create/edit/block/delete/link). */
    UsersManage: 'customers.users.manage',
  },
  Containers: {
    View: 'containers.view',
    Create: 'containers.create',
    Edit: 'containers.edit',
    Delete: 'containers.delete',
  },
  Equipment: {
    View: 'equipment.view',
    Create: 'equipment.create',
    Edit: 'equipment.edit',
    Delete: 'equipment.delete',
  },
  Services: {
    View: 'services.view',
    Create: 'services.create',
    Edit: 'services.edit',
    Delete: 'services.delete',
  },
  Pto: {
    View: 'pto.view',
    Create: 'pto.create',
    Edit: 'pto.edit',
    Delete: 'pto.delete',
  },
  Directions: {
    View: 'directions.view',
    Create: 'directions.create',
    Edit: 'directions.edit',
    Delete: 'directions.delete',
  },
  Materials: {
    View: 'materials.view',
    Create: 'materials.create',
    Edit: 'materials.edit',
    Delete: 'materials.delete',
  },
  Instructions: {
    View: 'instructions.view',
    Create: 'instructions.create',
    Edit: 'instructions.edit',
    Delete: 'instructions.delete',
  },
  Fmo: {
    PtoView: 'fmo.pto.view',
    PtoVerify: 'fmo.pto.verify',
    PtoCreateInvoice: 'fmo.pto.create_invoice',
    PtoUpdatePayment: 'fmo.pto.update_payment',
    /** Vision v4 Phase 4: unlock a verified PTO so mechanic can re-edit */
    PtoUnlock: 'fmo.unlock_pto',
    /** Vision v4 Phase 4: edit mileage for a PTO */
    PtoEditMileage: 'fmo.pto.edit_mileage',
  },
  AuditLogs: {
    View: 'audit_logs.view',
  },
  // Client portal release Р6 — «Почтовый шлюз»
  EmailGateway: {
    View: 'email_gateway.view',
    Process: 'email_gateway.process',
    /** Domain rules and the assign dialog's "remember this address" checkbox — stops at the head of the desk (blockers B3/B4). */
    RulesManage: 'email_gateway.rules.manage',
  },
  Messenger: {
    /**
     * Завести дополнительный чат с компанией-заказчиком и решить, кто в нём с обеих сторон.
     *
     * Прячет пункт «Чат с клиентом» в меню создания и секцию «Чаты компании» в карточке заказчика.
     * Мессенджер как раздел этим ключом НЕ закрывается — он открыт всем внутренним ролям.
     */
    CustomerChatsManage: 'messenger.customer_chats.manage',
  },
  Permissions: {
    MatrixView: 'permissions.matrix.view',
    MatrixEdit: 'permissions.matrix.edit',
    MatrixReset: 'permissions.matrix.reset',
  },
  Legal: {
    Manage: 'legal.manage',
  },
  Settings: {
    Manage: 'settings.manage',
  },
  AccountDeletion: {
    Review: 'account_deletion.review',
  },
  Tables: {
    ConfigView: 'tables.config.view',
    ConfigManage: 'tables.config.manage',
  },
  // Vision v4 Phase 1 — Stations & Terminals directory
  Stations: {
    View: 'directory.stations.view',
    Edit: 'directory.stations.edit',
    MobileCreate: 'mobile.stations.create',
  },
  Terminals: {
    View: 'directory.terminals.view',
    Edit: 'directory.terminals.edit',
  },
  // Vision v4 Phase 2 — Pricing
  Pricing: {
    View: 'directory.pricing.view',
    Edit: 'directory.pricing.edit',
  },
  // Vision v4 Phase 5 — FMO & Payroll
  FmoPhase5: {
    RecalculatePrices: 'fmo.recalculate_prices',
    ActMaterialsEdit: 'fmo.act_materials.edit',
    PaymentAdjustmentsView: 'fmo.payment_adjustments.view',
  },
  Payroll: {
    View: 'payroll.view',
    Export: 'payroll.export',
  },
  ContainerAttributes: {
    View: 'directory.container_attributes.view',
    Edit: 'directory.container_attributes.edit',
  },
  InspectionReasons: {
    View: 'directory.inspection_reasons.view',
    Edit: 'directory.inspection_reasons.edit',
  },
  // PTO crew, Phase 4 — mechanic types directory
  MechanicTypes: {
    View: 'directory.mechanic_types.view',
    Edit: 'directory.mechanic_types.edit',
  },
} as const;

/**
 * Sidebar tab → permission key required to render the tab.
 * Used by `useTabPermission(module)`.
 */
export const TAB_PERMISSION: Record<string, PermissionKey> = {
  applications: PK.Applications.View,
  tasks: PK.Tasks.ViewOwn,
  ownExpenses: PK.Expenses.OwnView,
  allExpenses: PK.Expenses.AllView,
  workers: PK.Workers.View,
  customers: PK.Customers.View,
  containers: PK.Containers.View,
  equipment: PK.Equipment.View,
  services: PK.Services.View,
  ptoDirectory: PK.Pto.View,
  directions: PK.Directions.View,
  materials: PK.Materials.View,
  instructions: PK.Instructions.View,
  fmo: PK.Fmo.PtoView,
  auditLogs: PK.AuditLogs.View,
  permissions: PK.Permissions.MatrixView,
  emailGateway: PK.EmailGateway.View,
};
